import { useState } from "react";
import { toast } from "sonner";
import customsClient, { setCustomsToken } from "@/lib/customsApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Ship, Mail, ArrowLeft, ShieldCheck } from "lucide-react";

const LOGO = "/tilesmate-logo.png";

export default function CustomsLogin() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Enter your email address"); return; }
    setBusy(true);
    try {
      await customsClient.post("/customs/auth/request-otp", { email: email.trim() });
      toast.success("If your email is approved, a code is on its way.");
      setStep("code");
    } catch { toast.error("Something went wrong — please try again."); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.trim().length < 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const { data } = await customsClient.post("/customs/auth/verify-otp", { email: email.trim(), code: code.trim() });
      setCustomsToken(data.session_token);
      toast.success("Signed in");
      window.location.assign("/customs");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid or expired code"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={LOGO} alt="TilesMate" className="mb-4 h-12 w-auto" />
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary"><Ship className="h-3.5 w-3.5" />Customs Portal</div>
          <h1 className="mt-3 font-display text-2xl font-bold text-ink">Shipments dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Secure sign-in for approved customs agents.</p>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgba(35,52,90,0.08)]">
          {step === "email" ? (
            <div className="space-y-4" data-testid="customs-email-step">
              <div className="grid gap-1.5">
                <Label className="text-xs">Work email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && requestCode()} placeholder="you@company.com" autoFocus data-testid="customs-email-input" />
              </div>
              <Button className="w-full gap-2" onClick={requestCode} disabled={busy} data-testid="customs-request-otp-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}Send me a code</Button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" />Only pre-approved emails can access this portal.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="customs-code-step">
              <button onClick={() => setStep("email")} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-ink"><ArrowLeft className="h-3.5 w-3.5" />Use a different email</button>
              <div className="grid gap-1.5">
                <Label className="text-xs">Enter the 6-digit code sent to <span className="font-semibold text-ink">{email}</span></Label>
                <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && verify()} placeholder="••••••" autoFocus className="text-center text-2xl tracking-[0.5em]" data-testid="customs-code-input" />
              </div>
              <Button className="w-full gap-2" onClick={verify} disabled={busy} data-testid="customs-verify-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}Verify & sign in</Button>
              <button onClick={requestCode} disabled={busy} className="w-full text-center text-xs font-medium text-primary hover:underline" data-testid="customs-resend-btn">Resend code</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
